import { useState, useCallback, useEffect } from 'react';

const CODE_REGEX = /^\d{6}$/;

interface CreateCodesDialogProps {
  open: boolean;
  loading: boolean;
  onConfirm: (codes: string[], generateCount: number, note: string) => void;
  onCancel: () => void;
}

export function CreateCodesDialog({ open, loading, onConfirm, onCancel }: CreateCodesDialogProps) {
  const [codesInput, setCodesInput] = useState('');
  const [generateCount, setGenerateCount] = useState('');
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!open) {
      setCodesInput('');
      setGenerateCount('');
      setNote('');
      setValidationError('');
    }
  }, [open]);

  const handleConfirm = useCallback(() => {
    setValidationError('');

    const codes = codesInput
      .split(/[,，\s\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const count = generateCount ? parseInt(generateCount, 10) : 0;

    if (codes.length === 0 && count <= 0) {
      setValidationError('请至少输入邀请码或填写生成数量');
      return;
    }

    for (const code of codes) {
      if (!CODE_REGEX.test(code)) {
        setValidationError(`邀请码格式错误：${code}，应为 6 位数字`);
        return;
      }
    }

    if (generateCount && (isNaN(count) || count < 0)) {
      setValidationError('生成数量应为非负整数');
      return;
    }

    onConfirm(codes, count, note.trim());
  }, [codesInput, generateCount, note, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={loading ? undefined : onCancel} />
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-4 p-6 space-y-4">
        <h3 className="text-base font-medium text-gray-900">创建邀请码</h3>
        <p className="text-xs text-gray-500">手工输入邀请码和批量随机生成可同时使用，至少其一必填。</p>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">手工输入邀请码</label>
          <textarea
            value={codesInput}
            onChange={(e) => setCodesInput(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none font-mono"
            placeholder="每个 6 位数字，逗号/换行/空格分隔"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">批量随机生成数量</label>
          <input
            type="number"
            value={generateCount}
            onChange={(e) => setGenerateCount(e.target.value)}
            min={0}
            className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="输入数量，如 10"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">备注</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="可选"
          />
        </div>

        {validationError && (
          <p className="text-xs text-red-500">{validationError}</p>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center gap-2"
          >
            {loading && (
              <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            创建
          </button>
        </div>
      </div>
    </div>
  );
}
